import { type Cta, ctaSchema, type ProjectDto, frameSize, type ShotData } from '@studio/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Clapperboard,
  Download,
  Film,
  Images,
  Loader2,
  Plus,
  Sparkles,
  Square,
  UsersRound,
  Wand2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { GpuCard } from '@/components/GpuCard';
import { useProjectCall, usePromptContext } from '@/components/project/common';
import { CharactersTab } from '@/components/project/CharactersTab';
import { DirectorTab } from '@/components/project/DirectorTab';
import { FramesTab } from '@/components/project/FramesTab';
import { ShotCard } from '@/components/project/ShotCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ProgressBar,
  SwitchRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/controls';
import { Field, Input } from '@/components/ui/form';
import { ErrorAlert, Skeleton } from '@/components/ui/misc';
import { errorMessage } from '@/lib/api';
import { isActive, projectBusy, useCharacters, useProject } from '@/lib/queries';
import { ago } from '@/lib/utils';

function Storyboard({ project }: { project: ProjectDto }) {
  const call = useProjectCall(project.id);
  const { data: characters } = useCharacters();
  const ctx = usePromptContext(project, characters);
  const [open, setOpen] = useState<string | null>(null);
  const shots = project.shots.filter((s) => s.kind === 'shot');
  const done = shots.filter((s) => s.takes.some((t) => t.id === s.chosenTakeId && t.status === 'succeeded')).length;
  const running = project.shots.some((s) => s.takes.some((t) => isActive(t.status)));
  const missing = shots.length - done;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="mr-auto min-w-48">
          <div className="text-sm font-medium">
            {done} of {shots.length} shots made
          </div>
          <ProgressBar value={shots.length ? done / shots.length : 0} tone="success" className="mt-1 max-w-xs" />
        </div>
        <Button
          onClick={() =>
            call(
              `/api/projects/${project.id}/generate`,
              { method: 'POST', body: { missingOnly: true } },
              (r: { project: ProjectDto }) => r.project,
            )
          }
          disabled={!missing}
          title={missing ? '' : 'Every shot has a video'}
        >
          <Sparkles />{' '}
          {!missing ? 'All shots made' : missing === shots.length ? 'Generate all' : `Generate ${missing} missing`}
        </Button>
        {running && (
          <Button variant="outline" onClick={() => call(`/api/projects/${project.id}/cancel`, { method: 'POST' })}>
            <Square /> Cancel all
          </Button>
        )}
      </div>
      {!project.shots.length && (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          No shots yet. Write a brief on the <b>Director</b> tab and send its plan here, or add shots by hand.
        </Card>
      )}
      <div className="space-y-2">
        {project.shots.map((s, i) => (
          <ShotCard
            key={s.id}
            project={project}
            shot={s}
            index={i}
            total={project.shots.length}
            ctx={ctx}
            characters={characters ?? []}
            expanded={open === s.id}
            onToggle={() => setOpen(open === s.id ? null : s.id)}
          />
        ))}
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={() => call(`/api/projects/${project.id}/shots`, { method: 'POST', body: { kind: 'shot' } })}
        >
          <Plus /> Add shot
        </Button>
        <Button
          variant="outline"
          onClick={() => call(`/api/projects/${project.id}/shots`, { method: 'POST', body: { kind: 'card' } })}
        >
          <Plus /> Add text card
        </Button>
      </div>
    </div>
  );
}

function FinishTab({ project }: { project: ProjectDto }) {
  const call = useProjectCall(project.id);
  const [cta, setCta] = useState<Cta>(project.cta);
  useEffect(() => setCta(project.cta), [project.cta]);
  const set = <K extends keyof Cta>(k: K, v: Cta[K]) => setCta((c) => ctaSchema.parse({ ...c, [k]: v }));
  const missing = project.shots
    .map((s, i) => ({ s, n: i + 1 }))
    .filter(({ s }) => s.kind === 'shot' && !s.takes.some((t) => t.id === s.chosenTakeId && t.status === 'succeeded'));
  const total = project.shots.reduce((n, s) => {
    if (s.kind === 'card') return n + (s.data as { seconds: number }).seconds;
    const t = s.takes.find((x) => x.id === s.chosenTakeId);
    return n + (t?.duration ?? (s.data as ShotData).seconds);
  }, 0);
  const assembling = project.finishStatus === 'assembling';
  const finish = async () => {
    await call(`/api/projects/${project.id}`, { method: 'PATCH', body: { cta } });
    await call(`/api/projects/${project.id}/finish`, { method: 'POST' });
  };
  return (
    <div className="grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
      <Card>
        <CardHeader>
          <CardTitle>Call to action</CardTitle>
          <CardDescription>Shown over the last shot. The logo is top-right all through.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3">
          <SwitchRow label="Show the call to action" checked={cta.enabled} onCheckedChange={(v) => set('enabled', v)} />
          <Field label="Button text">
            {(p) => <Input {...p} value={cta.buttonText} onChange={(e) => set('buttonText', e.target.value)} />}
          </Field>
          <Field label="Label above the button">
            {(p) => <Input {...p} value={cta.linkLabel} onChange={(e) => set('linkLabel', e.target.value)} />}
          </Field>
          <Field label="Starts this many seconds into the last shot">
            {(p) => (
              <Input
                {...p}
                type="number"
                min={0}
                max={30}
                step={0.5}
                value={cta.startSec}
                onChange={(e) => set('startSec', Number(e.target.value) || 0)}
              />
            )}
          </Field>
          <SwitchRow label="Bouncing arrow" checked={cta.showArrow} onCheckedChange={(v) => set('showArrow', v)} />
          <SwitchRow label="Logo top-right" checked={cta.showLogo} onCheckedChange={(v) => set('showLogo', v)} />
          <Button
            variant="outline"
            onClick={() =>
              call(`/api/projects/${project.id}`, { method: 'PATCH', body: { cta } }).then(
                (r) => r && toast.success('Saved'),
              )
            }
          >
            Save
          </Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Finished video</CardTitle>
          <CardDescription>
            Joins the chosen take of every shot and the cards, in order — about {total.toFixed(1)} s.{' '}
            {frameSize(project.aspect, project.size).outWidth}×{frameSize(project.aspect, project.size).outHeight}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {missing.length > 0 && (
            <ErrorAlert
              title="Some shots have no video yet"
              message={`Shots ${missing.map((m) => m.n).join(', ')} — generate them on the Storyboard tab first.`}
            />
          )}
          {project.finishStatus === 'failed' && project.finishError && (
            <ErrorAlert title="The last attempt failed" message={project.finishError} />
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => void finish()} disabled={assembling || missing.length > 0}>
              {assembling ? <Loader2 className="animate-spin" /> : <Clapperboard />}{' '}
              {assembling
                ? 'Putting the video together…'
                : project.finalVideo
                  ? 'Make it again'
                  : 'Make the final video'}
            </Button>
            {project.finalVideo && !assembling && (
              <Button variant="outline" asChild>
                <a href={`${project.finalVideo.url}?download=1`} download>
                  <Download /> Download MP4
                </a>
              </Button>
            )}
            {project.finishedAt && (
              <span className="text-xs text-muted-foreground">made {ago(project.finishedAt)}</span>
            )}
          </div>
          {project.finalVideo && (
            <video
              key={project.finalVideo.url + project.finishedAt}
              src={project.finalVideo.url}
              controls
              className={project.aspect === '9:16' ? 'max-h-[640px] rounded-md bg-black' : 'w-full rounded-md bg-black'}
            />
          )}
          <p className="text-xs text-muted-foreground">
            Before posting: label it as AI-generated, and do not show it in the EU, UK, USA or South Korea (MiniMax H3
            licence).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export function ProjectPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const asked = params.get('tab') ?? 'storyboard';
  const tab = asked === 'script' ? 'director' : asked;
  const q = useProject(id);
  useEffect(() => {
    // The projects list shows progress too.
    if (q.data) void qc.invalidateQueries({ queryKey: ['projects'] });
  }, [q.data?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  if (q.error) return <ErrorAlert message={errorMessage(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  const p = q.data;
  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="flex flex-wrap items-start gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/" aria-label="Back to projects">
            <ArrowLeft />
          </Link>
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold tracking-tight">{p.title}</h1>
          <div className="mt-1 flex flex-wrap gap-1.5 text-sm">
            {p.isDemo && <Badge>demo</Badge>}
            <Badge variant="secondary">{p.product?.title ?? 'no product'}</Badge>
            <Badge variant="secondary">{p.language}</Badge>
            <Badge variant="secondary">{p.aspect}</Badge>
            <Badge variant="outline">{p.style}</Badge>
            {projectBusy(p) && (
              <Badge variant="warning">
                <Loader2 className="animate-spin" /> working
              </Badge>
            )}
          </div>
        </div>
        <div className="w-full sm:w-72">
          <GpuCard compact />
        </div>
      </div>
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v }, { replace: true })}>
        <TabsList>
          <TabsTrigger value="director">
            <Wand2 /> 1. Director
          </TabsTrigger>
          <TabsTrigger value="characters">
            <UsersRound /> 2. Characters
          </TabsTrigger>
          <TabsTrigger value="frames">
            <Images /> 3. Storyboard stills
          </TabsTrigger>
          <TabsTrigger value="storyboard">
            <Film /> 4. Shots & videos
          </TabsTrigger>
          <TabsTrigger value="finish">
            <Clapperboard /> 5. Finish
          </TabsTrigger>
        </TabsList>
        <TabsContent value="director">
          <DirectorTab key={p.id} project={p} onSent={() => setParams({ tab: 'storyboard' }, { replace: true })} />
        </TabsContent>
        <TabsContent value="storyboard">
          <Storyboard project={p} />
        </TabsContent>
        <TabsContent value="characters">
          <CharactersTab project={p} />
        </TabsContent>
        <TabsContent value="frames">
          <FramesTab project={p} />
        </TabsContent>
        <TabsContent value="finish">
          <FinishTab project={p} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
